import { useSelector } from 'react-redux';

/*
 * usePageTitle() provides a descriptive webpage title (in the <head> element)
 *
 * A user may set a custom title on any given page using the hook, otherwise the
 * title will attempt to load the title of the page as it is defined in the navigation.
 *
 * @param {string} title - page title string
 * @param {object} intl - intl() object (mandatory)
 * @returns string
 *
 * example usage: usePageTitle('TITLE_STRING', intl);
 */

import { getNavItems } from 'selectors/navigation/mainNav';
import { isEverbridgeFlowDesignerApp } from 'selectors/featureToggles';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const findPageNavTitle = (navItems, loc) => {
  let navTitle;

  navItems?.forEach(navItem => {
    if (loc.pathname.toLowerCase().includes(navItem.to.toLowerCase())) {
      navTitle = navItem.textCode;
    }
    navItem?.subNav?.forEach((subNavItem, key) => {
      if (
        loc.pathname
          .toLowerCase()
          .includes(subNavItem?.items[key]?.to.toLowerCase())
      ) {
        navTitle = subNavItem?.items[key]?.id;
      }
      subNavItem?.items?.forEach(tertiarySubNavItem => {
        if (
          loc.pathname
            .toLowerCase()
            .includes(tertiarySubNavItem?.to.toLowerCase())
        ) {
          navTitle = tertiarySubNavItem?.id;
        }
      });
    });
  });
  return navTitle;
};

export function usePageTitle(title, intl) {
  const location = useLocation();

  if (location.pathname.includes('.do')) {
    return;
  }

  if (!intl) {
    throw new Error(`usePageTitle: 'intl' parameter is required`);
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navItems = useSelector(getNavItems);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const everbridgeFlowDesignerApp = useSelector(isEverbridgeFlowDesignerApp);
  const registeredMark = String.fromCodePoint(0x000ae);
  const company = everbridgeFlowDesignerApp
    ? `Everbridge${registeredMark}`
    : `xMatters${registeredMark}`;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    document.title = company;
    if (title) {
      document.title = `${intl.formatMessage({
        id: title,
      })} - ${company}`;
    } else if (findPageNavTitle(navItems, location)) {
      document.title = `${intl.formatMessage({
        id: findPageNavTitle(navItems, location),
      })} - ${company}`;
    }
  }, [location, title, navItems, intl, registeredMark, company]);
}
